/**
 * L'objectif de la séance d'un élève, et les matières qu'il dit — ce que l'ancienne fiche du live montrait à côté
 * du prénom (`ObjectiveSubjectsBadges`) et, en stage, dans le bandeau « Matière de la séance »
 * (`SessionSubjectBanner`). Décidé le 02/11 : **toujours visible**, en live régulier comme en stage, une seule
 * forme (le registre, O-01 et O-02).
 *
 * Port littéral de deux lectures du legacy, sans donnée neuve :
 *  - `resolveSessionObjective` (`common/components/objectives/ObjectiveSubjectsBadges.js`, lu aussi par
 *    `StudentProfil.js` et `NewStudentsListItem.js`) : le premier objectif pédagogique non clos de l'élève
 *    rattaché à la séance ;
 *  - la résolution des matières de `SessionSubjectBanner.js` : celles de cet objectif, à défaut ses matières
 *    prioritaires, à défaut ses matières.
 */
import { toUISubject, type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"
import { type UISubject } from "../../../ui/types/schoolSubject"

/** Un objectif pédagogique tel que le serveur le sert sur l'élève (`PedagogicalObjectiveResource`). */
export type ApiSessionObjective = {
    id?: number | string | null
    status?: string | null
    type?: string | null
    subject_ids?: (number | string)[] | null
    live_sessions?: ({ id?: number | string | null } | null)[] | null
}

/** Ce que ces lectures prennent de l'élève : ses objectifs et ses matières. */
export type ApiObjectiveStudent = {
    pedagogical_objectives?: (ApiSessionObjective | null)[] | null
    pedagogical_objective?: ApiSessionObjective | null
    prioritized_school_subjects?: ({ id?: number | string | null } | null)[] | null
    school_subjects?: ({ id?: number | string | null } | null)[] | null
}

/**
 * L'objectif travaillé dans cette séance : le premier objectif non clos de l'élève lié à la séance, ou `null`.
 * L'ancien comparait `ls?.id === liveSessionId` en strict : les ids du serveur sont des nombres des deux côtés.
 */
export function resolveSessionObjective(
    student: ApiObjectiveStudent | null | undefined,
    liveSessionId: number | string | null | undefined
): ApiSessionObjective | null {
    if (!Array.isArray(student?.pedagogical_objectives)) return null
    return (
        student.pedagogical_objectives
            .filter((objective): objective is ApiSessionObjective => objective != null && objective.status !== "closed")
            .find(
                (objective) =>
                    Array.isArray(objective.live_sessions) &&
                    objective.live_sessions.some((session) => session?.id === liveSessionId)
            ) ?? null
    )
}

/**
 * Les ids des matières de la séance, dans l'ordre du serveur : l'objectif, à défaut les matières prioritaires,
 * à défaut les matières (`SessionSubjectBanner`).
 */
export function sessionSubjectIds(
    student: ApiObjectiveStudent | null | undefined,
    objective: ApiSessionObjective | null
): (number | string)[] {
    if ((objective?.subject_ids?.length ?? 0) > 0) return objective!.subject_ids!
    const fallback =
        (student?.prioritized_school_subjects?.length ?? 0) > 0
            ? student!.prioritized_school_subjects!
            : (student?.school_subjects ?? [])
    return fallback.map((subject) => subject?.id).filter((id): id is number | string => id != null)
}

/**
 * Les matières de la séance, prêtes à montrer. Une matière que le référentiel ne connaît pas tombe (l'ancien
 * `.filter(Boolean)` après la recherche par id, comparée large : `s?.id == id`).
 */
export function toSessionSubjects(
    student: ApiObjectiveStudent | null | undefined,
    liveSessionId: number | string | null | undefined,
    subjectsById: Record<string, ApiSchoolSubject>
): UISubject[] {
    const objective = resolveSessionObjective(student, liveSessionId)
    return sessionSubjectIds(student, objective)
        .map((id) => subjectsById[String(id)])
        .filter((subject): subject is ApiSchoolSubject => subject != null)
        .map((subject) => toUISubject(subject))
}

/**
 * L'objectif que la fiche de l'élève ouverte en live lit pour son brief (`NewStudentsListItem.js:1568`) : celui de
 * la séance, sinon `pedagogical_objective`, sinon le premier de la liste.
 */
export function briefObjectiveId(
    student: ApiObjectiveStudent | null | undefined,
    liveSessionId: number | string | null | undefined
): number | string | null {
    return (
        resolveSessionObjective(student, liveSessionId)?.id ||
        student?.pedagogical_objective?.id ||
        student?.pedagogical_objectives?.[0]?.id ||
        null
    )
}
